import React, { useCallback, useEffect, useState } from "react";
import { useLifeAreas } from "../../Explore/lifeAreas";
import { useNavigate } from "react-router-dom";
import { Radar, RefreshCw } from "lucide-react";
import { listResearchPulseClusters } from "../../../Services/adminResearchPulse";
import EmptyState from "../../../Components/admin/shared/EmptyState";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import ClusterTable from "./ClusterTable";
import DigestPanel from "./DigestPanel";
import {
  CLUSTER_LIST_LIMIT,
  STATUSES,
  WINDOW_OPTIONS,
  buildClusterParams,
  clusterCountLabel,
  errorMessage,
  lifeAreaOptions,
  statusMeta,
} from "./researchPulseFormat";

/** The "what this is / isn't" line every Research Pulse screen carries. */
export const TriageDisclaimer = () => (
  <p className="text-sm text-muted-foreground">
    Recurring member needs that no canonical Dialogue covers. Triage records a
    research decision only — it never creates, certifies or publishes a Dialogue.
  </p>
);

const ClusterList = ({ onOpen, reloadKey }) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("all");
  const [area, setArea] = useState("all");
  const [windowDays, setWindowDays] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listResearchPulseClusters(
        buildClusterParams({ status, area, windowDays, limit: CLUSTER_LIST_LIMIT })
      );
      setItems(data?.items || []);
      setCount(data?.count ?? 0);
    } catch (err) {
      setError(errorMessage(err, "Failed to load Coverage Signal clusters"));
      setItems([]);
    } finally {
      setLoading(false);
    }
    // reloadKey forces a refetch from the page's Refresh button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, area, windowDays, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = [status, area, windowDays].some((v) => v !== "all");

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full lg:w-[200px]" aria-label="Status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {statusMeta(s).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-full lg:w-[220px]" aria-label="Life area">
              <SelectValue placeholder="Life area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All life areas</SelectItem>
              {lifeAreaOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={windowDays} onValueChange={setWindowDays}>
            <SelectTrigger className="w-full lg:w-[180px]" aria-label="Last seen">
              <SelectValue placeholder="Last seen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Seen any time</SelectItem>
              {WINDOW_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Radar size={40} />}
            title={filtered ? "No matching clusters" : "No Coverage Signals yet"}
            description={
              filtered
                ? "No clusters match your current filters."
                : "Clusters appear here when members bring needs no canonical Dialogue covers."
            }
          />
        ) : (
          <>
            <ClusterTable rows={items} onOpen={onOpen} />
            <p className="text-sm text-muted-foreground">
              {clusterCountLabel(count, CLUSTER_LIST_LIMIT)}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const AdminResearchPulse = () => {
  useLifeAreas(); // filter options and labels come from the backend's Life Areas
  const navigate = useNavigate();
  const [tab, setTab] = useState("clusters");
  const [reloadKey, setReloadKey] = useState(0);

  const openCluster = (clusterId) =>
    navigate(`/admin/research-pulse/${encodeURIComponent(clusterId)}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Research Pulse</h1>
          <TriageDisclaimer />
        </div>
        <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="clusters">Clusters</TabsTrigger>
          <TabsTrigger value="digest">Digest</TabsTrigger>
        </TabsList>
        <TabsContent value="clusters" className="mt-4">
          <ClusterList onOpen={openCluster} reloadKey={reloadKey} />
        </TabsContent>
        <TabsContent value="digest" className="mt-4">
          <DigestPanel reloadKey={reloadKey} onOpen={openCluster} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminResearchPulse;
